import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDownRight, ArrowRight } from "lucide-react";
import { useLang, scrollToId } from "./ctx";
import { MaskLines, EASE } from "./Motion";

const D = 1.5;

const HeroImage = ({ badge }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.08, 1.2]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [5, -5]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-6, 6]), { stiffness: 120, damping: 18 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };

  return (
    <div ref={ref} className="relative [perspective:1400px]" onMouseMove={onMove} onMouseLeave={reset}>
      <motion.div style={{ rotateX: rx, rotateY: ry }} className="relative [transform-style:preserve-3d]">
        <motion.div
          initial={{ clipPath: "inset(100% 0% 0% 0%)" }}
          animate={{ clipPath: "inset(0% 0% 0% 0%)" }}
          transition={{ duration: 1.5, ease: EASE, delay: D + 0.2 }}
          className="relative aspect-[4/5] overflow-hidden rounded-t-[999px] bg-stone-200 lg:aspect-[4/5]"
        >
          <motion.img style={{ y, scale }} src="/assets/hero.jpg" alt="Kaolin quarry" className="h-full w-full object-cover" data-testid="hero-image" />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: EASE, delay: D + 1 }}
          style={{ transform: "translateZ(60px)" }}
          className="absolute -left-3 bottom-8 max-w-[calc(100%-1rem)] rounded-2xl border border-white/60 bg-white/70 px-4 py-3 sm:bottom-10 sm:px-5 sm:py-4 shadow-[0_20px_60px_-20px_rgba(20,18,15,0.35)] backdrop-blur-xl sm:-left-10"
          data-testid="hero-badge"
        >
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-stone-500">Kaolin · Al₂Si₂O₅(OH)₄</div>
          <div className="mt-1 font-display text-base italic text-stone-900 sm:text-xl">{badge}</div>
        </motion.div>
        <div className="absolute -right-3 -top-3 h-16 w-16 border-r border-t border-[#A8874F]" style={{ transform: "translateZ(30px)" }} />
      </motion.div>
    </div>
  );
};

export const Hero = () => {
  const { t, lang } = useLang();
  return (
    <section id="top" className="relative overflow-hidden pb-16 pt-28 sm:pb-20 sm:pt-32 lg:pb-24 lg:pt-36" data-testid="hero-section">
      <div className="grain pointer-events-none absolute inset-0" />
      <div className="mx-auto grid max-w-[1440px] items-end gap-12 px-6 lg:grid-cols-12 lg:gap-10 lg:px-12">
        <div className="lg:col-span-7">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: D, duration: 1 }} className="mb-8 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.3em] text-stone-500">
            <span className="h-px w-10 bg-[#A8874F]" />{t.hero.eyebrow}
          </motion.div>
          <h1 key={lang} data-testid="hero-headline" className="font-display text-[2.9rem] font-light leading-[1] tracking-[-0.03em] text-stone-950 sm:text-6xl lg:text-[5.4rem]">
            <MaskLines lines={t.hero.lines} animateOnLoad delay={D} />
            <MaskLines lines={[t.hero.accent]} animateOnLoad delay={D + 0.33} lineClass="italic text-[#A8874F]" />
          </h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: D + 0.7, duration: 1, ease: EASE }} className="mt-8 max-w-xl text-base sm:mt-10 leading-relaxed text-stone-600 md:text-lg">
            {t.hero.sub}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: D + 0.85, duration: 1, ease: EASE }} className="mt-10 grid grid-cols-1 gap-3 sm:flex sm:flex-wrap sm:gap-4">
            <button data-testid="hero-cta-specs" onClick={() => scrollToId("kaolin")} className="group flex items-center justify-center gap-3 rounded-full bg-[#14120F] px-7 py-4 text-sm tracking-wide text-white transition-colors duration-300 hover:bg-[#A8874F]">
              {t.hero.cta1}
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button data-testid="hero-cta-contact" onClick={() => scrollToId("contact")} className="rounded-full border border-stone-300 px-7 py-4 text-sm tracking-wide text-stone-900 transition-colors duration-300 hover:border-stone-900">
              {t.hero.cta2}
            </button>
          </motion.div>
        </div>
        <div className="mx-auto w-full max-w-md lg:col-span-5 lg:max-w-none">
          <HeroImage badge={t.hero.badge} />
        </div>
      </div>
      <motion.button
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: D + 1.4 }}
        onClick={() => scrollToId("stats")} data-testid="hero-scroll-indicator"
        className="mx-auto mt-16 hidden items-center gap-2 px-12 font-mono text-[10px] uppercase tracking-[0.3em] text-stone-500 lg:flex lg:max-w-[1440px]"
      >
        <ArrowDownRight size={14} className="animate-bounce" /> {t.hero.scroll}
      </motion.button>
    </section>
  );
};
